import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isPublicStartOpen, startFitting } from '../lib/api'
import { lastFittingRoom } from '../lib/lastFittingRoom'
import { uiStrings } from '../lib/strings'

/**
 * The boutique's front door.
 *
 * What this replaced: `DevIndexRoute`, a page whose own heading read "Fitting
 * Room — dev index" and whose own copy called itself scaffolding, while being
 * the page a bride handed the shop's domain actually landed on. It also offered
 * a staff-token field, a visit list and a paste-a-session-token box to anyone
 * who opened it. None of that belongs on a customer's first screen; the parts
 * of it a developer genuinely uses moved to `/staff`, behind the same gate the
 * console is behind.
 *
 * There is no way through to `/staff` from here, not even a quiet link at the
 * foot. Staff arrive at their own address — they are told it once and bookmark
 * it — so a link would only ever be read by the customer, and the one reader it
 * has is the one person it is not for. Nothing about the boutique's back office
 * belongs on the screen a bride is handed.
 *
 * Two paths render this, on purpose:
 *
 *  - **`/`** — a walk-in is handed the boutique's domain, or scans a code in the
 *    window. A door at some other path is a door only someone who already knew
 *    the path could find.
 *  - **`/start`** — the same door, at a name a stylist can be told. It is what
 *    the console links to: a bride is standing at the desk without a booking,
 *    and the stylist needs to hand her a tablet that is already on this screen.
 *    That was previously only reachable via the dev index, which is to say the
 *    real use case ran through the page that called itself scaffolding.
 *
 * When `FITTING_ROOM_PUBLIC_START` is off there is no door to open, and this
 * says so rather than offering a button that 404s. Which it is, is asked
 * (`GET /api/start`) rather than compiled in — the client and the API are two
 * containers and a build flag would need a rebuild to change.
 */
export function CustomerHomeRoute() {
  const locale = navigator.language || 'en'
  const strings = uiStrings(locale)

  const publicStart = useQuery({
    queryKey: ['public', 'start'] as const,
    queryFn: ({ signal }) => isPublicStartOpen(signal),
    retry: false,
    staleTime: Infinity,
  })

  const start = useMutation({
    mutationFn: () => startFitting(),
    onSuccess: (started) => {
      // Relative on purpose: `url` is absolute and only right once
      // FITTING_ROOM_APP_URL is set, while this page is already on whatever
      // origin served it.
      window.location.assign(`/s/${encodeURIComponent(started.token)}`)
    },
  })

  const doorOpen = publicStart.data === true
  // Read once: a fitting room this phone already has, which is where she most
  // likely meant to go when she came back to the boutique's address.
  const [resumable] = useState(() => lastFittingRoom())

  return (
    <div className="fr-room min-h-screen">
      <div className="mx-auto flex min-h-screen w-full max-w-[520px] flex-col justify-center px-5 py-12">
        {resumable ? (
          <>
            <h1 className="fr-display text-[2rem] leading-[1.08]">{strings.resumeFittingTitle}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {strings.resumeFittingBody}
            </p>
            <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href={`/s/${encodeURIComponent(resumable)}`}>
              {strings.resumeFitting}
            </a>
            {doorOpen ? (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  className="fr-btn-quiet fr-btn-target"
                  disabled={start.isPending}
                  onClick={() => start.mutate()}
                >
                  {start.isPending ? strings.loading : strings.startAnother}
                </button>
              </div>
            ) : null}
            {start.isError ? (
              <p role="alert" className="mt-3 text-[0.875rem]" style={{ color: 'var(--fr-danger, #9f1239)' }}>
                {strings.startFittingFailed}
              </p>
            ) : null}
          </>
        ) : doorOpen ? (
          <>
            <h1 className="fr-display text-[2rem] leading-[1.08]">{strings.startFitting}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {strings.startFittingBody}
            </p>
            <button
              type="button"
              className="fr-btn fr-btn-primary mt-7 w-full"
              disabled={start.isPending}
              onClick={() => start.mutate()}
            >
              {start.isPending ? strings.loading : strings.startFitting}
            </button>
            {start.isError ? (
              <p role="alert" className="mt-3 text-[0.875rem]" style={{ color: 'var(--fr-danger, #9f1239)' }}>
                {strings.startFittingFailed}
              </p>
            ) : null}
          </>
        ) : (
          <>
            {/*
              No public door. She is here because she has a link and lost it, or
              because she typed the domain — so the useful thing to say is where
              the link came from, not that a feature is disabled.
            */}
            <h1 className="fr-display text-[2rem] leading-[1.08]">{strings.homeByAppointment}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {strings.homeByAppointmentBody}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
